
import { Field, layer_worker } from './Field.js';
import { hex2rgba } from './utils.js';

class FieldContour extends Field {
    constructor(field, args) {
        super(field, args);

        this.color = hex2rgba(this.color);
    }

    async onAdd(map, gl) {
        // Basic procedure for these contours from https://www.shadertoy.com/view/lltBWM
        this.map = map;

        gl.getExtension('OES_texture_float');
        gl.getExtension('OES_texture_float_linear');
        gl.getExtension('OES_standard_derivatives');

        const vertexSource = `
        uniform mat4 u_matrix;

        attribute vec2 a_pos;
        attribute vec2 a_tex_coord;
        attribute float a_latitude;

        varying highp vec2 v_tex_coord;
        varying highp float v_map_scale_fac;

        void main() {
            gl_Position = u_matrix * vec4(a_pos, 0.0, 1.0);
            v_tex_coord = a_tex_coord;
            v_map_scale_fac = cos(a_latitude * 3.141592654 / 180.);
        }`;
        
        // create GLSL source for fragment shader
        const fragmentSource = `
        #extension GL_OES_standard_derivatives : enable
        varying highp vec2 v_tex_coord;
        varying highp float v_map_scale_fac;

        uniform sampler2D u_fill_sampler;
        uniform highp float u_contour_interval;
        uniform lowp float u_line_cutoff;
        uniform lowp vec3 u_color;
        uniform lowp vec2 u_step_size;
        uniform lowp float u_zoom_fac;

        void main() {
            highp float field_val = texture2D(u_fill_sampler, v_tex_coord).r;

            // Find the gradient magnitude of the grid (the y component divides by 2 to cheat for high latitudes)
            lowp vec2 ihat = vec2(u_step_size.x, 0.0);
            lowp vec2 jhat = vec2(0.0, u_step_size.y);
            highp float fv_xp1 = texture2D(u_fill_sampler, v_tex_coord + ihat).r;
            highp float fv_xm1 = texture2D(u_fill_sampler, v_tex_coord - ihat).r;
            highp float fv_yp1 = texture2D(u_fill_sampler, v_tex_coord + jhat).r;
            highp float fv_ym1 = texture2D(u_fill_sampler, v_tex_coord - jhat).r;
            highp float fwidth_field = sqrt((fv_xp1 - fv_xm1) * (fv_xp1 - fv_xm1) + (fv_yp1 - fv_ym1) * (fv_yp1 - fv_ym1) * v_map_scale_fac * v_map_scale_fac) / 2.;

            //gl_FragColor = vec4(fwidth_field, fwidth_field, fwidth_field, 1.0);

            lowp float plot_val = fract(field_val / u_contour_interval);
            if (plot_val > 0.5) plot_val = 1.0 - plot_val;
            plot_val = plot_val / (max(0.001, fwidth_field / (u_zoom_fac * 0.125)));

            if (plot_val > u_line_cutoff) discard;

            gl_FragColor = vec4(u_color, 1. - (plot_val * plot_val / (u_line_cutoff * u_line_cutoff)));
        }`;
        
        this.program = this._compileAndLinkShaders(gl, vertexSource, fragmentSource);

        const verts_tex_coords = await layer_worker.makeDomainVerticesAndTexCoords(this.field);

        this.vertices = this._setupVertices(gl, this.program, verts_tex_coords['vertices'], 2, 'a_pos');
        this.latitudes = this._setupVertices(gl, this.program, new Float32Array([...this.field['lats']].map(lat => [lat, lat]).flat()), 1, 'a_latitude');

        const fill_image = {'format': gl.LUMINANCE, 'type': gl.FLOAT, 
            'width': this.field['tex_width'], 'height': this.field['tex_height'],
            'image': this.field['data'],
            'mag_filter': gl.LINEAR,
        };

        this.u_cnt_interval = gl.getUniformLocation(this.program, 'u_contour_interval');
        this.u_cutoff = gl.getUniformLocation(this.program, 'u_line_cutoff');
        this.u_color = gl.getUniformLocation(this.program, 'u_color');
        this.u_step_size = gl.getUniformLocation(this.program, 'u_step_size');
        this.u_zoom_fac = gl.getUniformLocation(this.program, 'u_zoom_fac');

        this.fill_texture = this._setupTexture(gl, this.program, fill_image, 'u_fill_sampler', 
            verts_tex_coords['tex_coords'], 'a_tex_coord');

        this.is_initialized = true;
    }

    render(gl, matrix) {
        if (!this.is_initialized) return;

        gl.useProgram(this.program);
        gl.uniformMatrix4fv(
            gl.getUniformLocation(this.program, 'u_matrix'),
            false,
            matrix
        );

        const zoom = this.map.getZoom();
        const intv = zoom < 5 ? this.interval * 2 : this.interval;
        const cutoff = 2 / intv;

        gl.uniform1f(this.u_cnt_interval, intv);
        gl.uniform1f(this.u_cutoff, cutoff);
        gl.uniform3f(this.u_color, ...this.color);
        gl.uniform2f(this.u_step_size, 0.25 / this.field['tex_width'], 0.25 / this.field['tex_height']);
        gl.uniform1f(this.u_zoom_fac, Math.pow(2, zoom));

        this._bindTexture(gl, 0, this.fill_texture);

        this._bindVertices(gl, this.vertices);
        this._bindVertices(gl, this.latitudes);

        gl.enable(gl.BLEND);
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

        gl.drawArrays(gl.TRIANGLE_STRIP, 0, this.field['nlats'] * 2);
    }
}

export default FieldContour;